import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Key, Printer, Plus, X, Check, FileDown, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const getPinLabel = (employee) => {
  if (!employee?.tem_pin) return "—";
  if (employee?.pin) return employee.pin;
  return "PIN legado";
};

const PinManagerPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchModeDialogOpen, setBatchModeDialogOpen] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(null);
  const [printMode, setPrintMode] = useState(false);
  const [batchPins, setBatchPins] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/ponto-app/pins`, authHeader());
      setEmployees(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar lista de PINs");
    } finally {
      setLoading(false);
    }
  };

  const gerarPin = async (id) => {
    setGenerating(id);
    try {
      const r = await axios.post(`${API}/api/ponto-app/pins/gerar?employee_id=${id}`, {}, authHeader());
      setShowPinDialog(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PIN");
    } finally {
      setGenerating(null);
    }
  };

  const gerarLote = async (modo) => {
    setBatchGenerating(true);
    try {
      const r = await axios.post(`${API}/api/ponto-app/pins/gerar-lote?modo=${modo}`, {}, authHeader());
      setBatchPins((r.data || []).map((item) => ({ ...item, tem_pin: true })));
      setPrintMode(true);
      setBatchModeDialogOpen(false);
      load();
      toast.success(
        modo === "todos"
          ? "Novos PINs gerados para todos os colaboradores elegíveis."
          : "PINs gerados para os colaboradores pendentes."
      );
      // Auto-download PDF após gerar lote
      setTimeout(() => exportPdf(), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PINs em lote");
    } finally {
      setBatchGenerating(false);
    }
  };

  const exportPdf = () => {
    const token = localStorage.getItem("token");
    const url = `${API}/api/ponto-app/pins/exportar-pdf`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao exportar PDF");
        return res.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "pins_ponto_eletronico.pdf";
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("PDF exportado com sucesso!");
      })
      .catch(() => toast.error("Erro ao exportar PDF"));
  };

  const exportarExcel = () => {
    const token = localStorage.getItem("token");
    const url = `${API}/api/ponto-app/pins/exportar-excel`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao exportar");
        return res.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "pins_ponto_eletronico.xlsx";
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("Planilha exportada com sucesso!");
      })
      .catch(() => toast.error("Erro ao exportar Excel"));
  };

  const printPins = () => {
    if (employees.some((e) => e.tem_pin)) {
      setPrintMode(true);
      setTimeout(() => { window.print(); }, 500);
    } else {
      toast.info("Nenhum PIN cadastrado. Use 'Gerar PINs em Lote' primeiro.");
    }
  };

  const resetPin = async (id) => {
    if (!window.confirm("Tem certeza? O PIN atual será substituído.")) return;
    gerarPin(id);
  };

  const pinsToShow = batchPins || employees;

  if (printMode) {
    return (
      <div className="p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold">Lista de PINs - Ponto Eletrônico</h1>
          <p className="text-slate-500">GridProfile</p>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-black">
              <th className="text-left p-2">Colaborador</th>
              <th className="text-left p-2">Departamento</th>
              <th className="text-center p-2">PIN</th>
              <th className="text-center p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {pinsToShow.filter((e) => e.tem_pin || e.pin).map((emp) => (
              <tr key={emp.employee_id} className="border-b border-gray-300">
                <td className="p-2">{emp.nome}</td>
                <td className="p-2">{emp.departamento || "—"}</td>
                <td className="p-2 text-center font-mono text-lg font-bold">{getPinLabel(emp)}</td>
                <td className="p-2 text-center">
                  {emp.situacao === "ativo" ? "Ativo" : emp.situacao}
                </td>
              </tr>
            ))}
            {pinsToShow.filter((e) => e.tem_pin || e.pin).length === 0 && (
              <tr>
                <td colSpan={4} className="text-center p-4 text-slate-500">
                  Nenhum PIN cadastrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="text-center mt-4 flex justify-center gap-2">
          <Button onClick={() => { setPrintMode(false); setBatchPins(null); }}>Fechar visualização</Button>
          {batchPins && (
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-1" />
              Imprimir
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gerenciar PINs</h1>
          <p className="text-sm text-slate-500">
            Gerencie os PINs de acesso ao Ponto-App ({employees.length} colaboradores)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Atualizar
          </Button>
          <Button variant="outline" onClick={exportarExcel}>
            <FileDown className="h-4 w-4 mr-1" />
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={exportPdf}>
            <FileText className="h-4 w-4 mr-1" />
            Exportar PDF
          </Button>
          <Button
            onClick={() => setBatchModeDialogOpen(true)}
            disabled={batchGenerating || loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {batchGenerating ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Key className="h-4 w-4 mr-1" />
            )}
            Gerar PINs em Lote
          </Button>
          <Button onClick={printPins} disabled={loading} variant="outline">
            <Printer className="h-4 w-4 mr-1" />
            Imprimir PINs
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Colaborador</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Tem PIN?</TableHead>
                <TableHead className="text-center">PIN</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Nenhum colaborador encontrado
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((emp) => (
                  <TableRow key={emp.employee_id}>
                    <TableCell className="font-medium">{emp.nome}</TableCell>
                    <TableCell className="text-slate-500">{emp.departamento || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={emp.situacao === "ativo" ? "default" : "secondary"}>
                        {emp.situacao || "ativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {emp.tem_pin ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Check className="h-3 w-3 mr-1" /> Sim
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-400">
                          <X className="h-3 w-3 mr-1" /> Não
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {emp.tem_pin ? (
                        emp.pin ? (
                          <span className="font-mono text-sm font-semibold text-slate-700">{emp.pin}</span>
                        ) : (
                          <span className="text-xs font-medium text-amber-700">Legado, regenerar</span>
                        )
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {emp.tem_pin ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resetPin(emp.employee_id)}
                          disabled={generating === emp.employee_id}
                        >
                          {generating === emp.employee_id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Key className="h-3 w-3 mr-1" />
                          )}
                          Redefinir
                        </Button>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => gerarPin(emp.employee_id)}
                          disabled={generating === emp.employee_id}
                        >
                          {generating === emp.employee_id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Plus className="h-3 w-3 mr-1" />
                          )}
                          Gerar PIN
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!showPinDialog} onOpenChange={(o) => !o && setShowPinDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>PIN Gerado</DialogTitle>
            <DialogDescription>
              Entregue este PIN ao colaborador. A visualização posterior fica restrita ao Grid.
            </DialogDescription>
          </DialogHeader>
          {showPinDialog && (
            <div className="text-center py-6">
              <p className="text-sm text-slate-500 mb-2">{showPinDialog.nome}</p>
              <div className="text-4xl font-mono font-bold tracking-widest text-primary bg-slate-100 rounded-lg py-4 px-6 inline-block">
                {showPinDialog.pin}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowPinDialog(null)} className="w-full">
              Anotei o PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batchModeDialogOpen} onOpenChange={setBatchModeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gerar PINs em lote</DialogTitle>
            <DialogDescription>
              Escolha se deseja gerar PINs apenas para quem ainda não possui PIN ou regenerar para todos os colaboradores elegíveis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              type="button"
              onClick={() => gerarLote("pendentes")}
              disabled={batchGenerating}
              className="w-full rounded-lg border border-slate-200 p-4 text-left transition hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-60"
            >
              <div className="font-medium text-slate-800">Somente pendentes</div>
              <div className="text-sm text-slate-500">
                Gera PIN só para colaboradores sem PIN cadastrado.
              </div>
            </button>
            <button
              type="button"
              onClick={() => gerarLote("todos")}
              disabled={batchGenerating}
              className="w-full rounded-lg border border-amber-200 p-4 text-left transition hover:border-amber-300 hover:bg-amber-50 disabled:opacity-60"
            >
              <div className="font-medium text-slate-800">Gerar para todos novamente</div>
              <div className="text-sm text-slate-500">
                Substitui os PINs atuais de todos os colaboradores elegíveis por novos PINs.
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchModeDialogOpen(false)} disabled={batchGenerating}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PinManagerPage;
